// @flow
import * as React from 'react';

import { useDispatch } from 'react-redux';

import { notifyDanger, notifySuccess } from '@gandi/notifications';
import { useTranslator } from '@gandi/translate';

type UseDragAndDropOptions = {
  setDomains: React.ReactSetStateFunction<string>,
  setFileName: React.ReactSetStateFunction<string>,
  setError?: React.ReactSetStateFunction<string>,
};

const NOTIFICATION_ID = 'bulk-import-notif';

export function useDragAndDrop({
  setDomains,
  setFileName,
  setError,
}: UseDragAndDropOptions): {
  bind: {
    onDragLeave: (
      e: SyntheticDragEvent<ReactDOM$divInstance | HTMLTextAreaElement>,
    ) => void,
    onDragOver: (
      e: SyntheticDragEvent<ReactDOM$divInstance | HTMLTextAreaElement>,
    ) => void,
    onDrop: (
      e: SyntheticDragEvent<ReactDOM$divInstance | HTMLTextAreaElement>,
    ) => void,
  },
  isDragging: boolean,
  handleFile: (file: File) => void,
} {
  const dispatch = useDispatch();
  const { __ } = useTranslator();

  const [isDragging, setIsDragging] = React.useState(false);

  const addError = React.useCallback(
    (err: string) => {
      dispatch(
        notifyDanger({
          id: NOTIFICATION_ID,
          message: err,
        }),
      );
      if (setError) setError(err);
    },
    [dispatch, setError],
  );

  const parseCSV = React.useCallback(
    (file: globalThis.File) => {
      const reader = new FileReader();
      reader.onload = e => {
        try {
          // $FlowFixMe[prop-missing]
          const text = e.target.result;

          if (!text || typeof text !== 'string') {
            throw new Error(__('Invalid file content'));
          }

          const lines = text.split(/\s*\n\s*/);

          if (!lines.filter(Boolean).length) {
            throw new Error(__('File is empty'));
          }

          setDomains(lines.filter(d => d.trim() !== '').join('\n'));
          setFileName(file.name);

          dispatch(
            notifySuccess({
              id: NOTIFICATION_ID,
              message: __('File uploaded successfully'),
            }),
          );
        } catch (error) {
          const message =
            error instanceof Error ? error.message : String(error);
          addError(message);
        }
      };

      reader.onerror = () => {
        addError(__('Error reading the file'));
      };

      reader.readAsText(file);
    },
    [__, addError, dispatch, setDomains, setFileName],
  );

  const handleFile = React.useCallback(
    (file: globalThis.File) => {
      if (!file) {
        addError(__('No file selected'));
        return;
      }

      const isCSV = file.type === 'text/csv' || file.name.endsWith('.csv');
      const isTSV =
        file.type === 'text/tab-separated-values' ||
        file.name.endsWith('.tsv') ||
        file.name.endsWith('.tab');
      const isText = file.type === 'text/plain' || file.name.endsWith('.txt');

      if (isCSV || isTSV || isText) {
        parseCSV(file);
      } else {
        addError(__('Please upload a valid .CSV file'));
      }
    },
    [__, addError, parseCSV],
  );

  const handleDrag = React.useCallback(
    (
      e: SyntheticDragEvent<ReactDOM$divInstance | HTMLTextAreaElement>,
      dragging: boolean,
    ) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(dragging);
    },
    [],
  );

  const handleDrop = React.useCallback(
    (e: SyntheticDragEvent<ReactDOM$divInstance | HTMLTextAreaElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const dataTransfer = e.dataTransfer;

      if (!dataTransfer) return;

      const file = dataTransfer.files?.[0];
      const text =
        'getData' in dataTransfer && dataTransfer.getData('text/plain');
      if (file) {
        handleFile(file);
      } else if (text) {
        setDomains(text);
      }
    },
    [handleFile, setDomains],
  );

  return {
    bind: {
      onDragLeave: (
        e: SyntheticDragEvent<ReactDOM$divInstance | HTMLTextAreaElement>,
      ) => handleDrag(e, false),
      onDragOver: e => handleDrag(e, true),
      onDrop: handleDrop,
    },
    handleFile,
    isDragging,
  };
}
